import { useEffect, useState, useMemo, useCallback } from "react";
import { setAppTheme } from "../Settings/appTheme";

export type ToolsTab = 'changehole' | 'scorecard' | 'challenges';

// Add interface for iOS Safari navigator extension
interface NavigatorWithStandalone extends Navigator {
    standalone?: boolean;
}

interface LSStates {
    factor: number;    setFactor: (factor: number) => void;
    appTheme: any;     setTheme: (theme: any) => void;
    redirectURL: string | null; setRedirectURL: (redirectURL: string | null) => void;
    imageCache: any; setImageCache: (imageCache: any) => void;
    windowSize: any; setWindowSize: (windowSize: any) => void;
    scoreLock: boolean; setScoreLock: (scoreLock: boolean) => void;
    currentHole: number; setCurrentHole: (currentHole: number) => void;
    scoreViewTabs: string; setScoreViewTabs: (scoreViewTabs: string) => void;
    roundReview: boolean; setRoundReview: (roundReview: boolean) => void;
    toggleScoring: boolean; setToggleScoring: (toggleScoring: boolean) => void;
    standingTabs: string; setStandingTabs: (standingTabs: string) => void;
    leaderboardScoreFormat: string; setLeaderboardScoreFormat: (leaderboardScoreFormat: string) => void;
    isPWA: boolean; setIsPWA: (isPWA: boolean) => void;
    scoreBlockID: string; setScoreBlockID: (scoreBlockID: string) => void;
    endOfRoundPopupOpen: boolean; setEndOfRoundPopupOpen: (open: boolean) => void;
    toolsOpen: boolean; setToolsOpen: (open: boolean) => void;
    toolsTab: ToolsTab; setToolsTab: (tab: ToolsTab) => void;
}

export function useLSStates() : LSStates {

    const [factor, setFactor] = useState(1);
    const [appTheme, setTheme] = useState(setAppTheme(1));

    const [redirectURL, setRedirectURL] = useState<string | null>(null);
    const [imageCache, setImageCache] = useState<any[]>([]);

    const [windowSize, setWindowSize] = useState<any>({
        width: window.innerWidth,
        height: window.innerHeight,
        landscape: window.innerWidth > window.innerHeight,
        verticalMobile: window.innerWidth < window.innerHeight && window.innerWidth < 450,
        horizontalMobile: window.innerWidth < window.innerHeight && window.innerHeight < 450,
        verticalPC: window.innerWidth < window.innerHeight && window.innerWidth <= 600 ? "small" : 
            window.innerWidth < window.innerHeight && window.innerWidth <= 900 ? "medium" : 
            window.innerWidth < window.innerHeight ? "large" : "none",
        horizontalPC: window.innerWidth > window.innerHeight && window.innerWidth < 900 ? "small" : 
            window.innerWidth > window.innerHeight && window.innerWidth <= 1200 ? "medium" : 
            window.innerWidth > window.innerHeight ? "large" : "none",
        isMobile: /Mobi|Android|iPhone|iPad|iPod|tablet|Tab/i.test(navigator.userAgent),
        isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
        isIpad: /iPad|Macintosh/.test(navigator.userAgent),
        platform: navigator.userAgent,
        appBarHeight: 60
    });
    
    const [scoreLock, setScoreLock] = useState(false);
    const [currentHole, setCurrentHole] = useState(1);
    const [scoreViewTabs, setScoreViewTabs] = useState('numpad');
    const [roundReview, setRoundReview] = useState(false);
    const [toggleScoring, setToggleScoring] = useState(false);
    const [standingTabs, setStandingTabs] = useState('individual');
    const [leaderboardScoreFormat, setLeaderboardScoreFormat] = useState('gross');

    // ... existing code ...
    const [isPWA, setIsPWA] = useState<boolean>(
        // For iOS devices
        Boolean((window.navigator as NavigatorWithStandalone).standalone) || 
        // For other platforms using display-mode media query
        window.matchMedia('(display-mode: standalone)').matches || 
        window.matchMedia('(display-mode: fullscreen)').matches || 
        window.matchMedia('(display-mode: minimal-ui)').matches)
    ;

    const [scoreBlockID, setScoreBlockID] = useState('');
    const [endOfRoundPopupOpen, setEndOfRoundPopupOpen] = useState(false);
    const [toolsOpen, setToolsOpen] = useState(false);
    const [toolsTab, setToolsTab] = useState<ToolsTab>('changehole');

    // Memoize the handleResize function to prevent recreating it on every render
    const handleResize = useCallback(() => {
        const newWindowSize = {
            width: window.innerWidth,
            height: window.innerHeight,
            landscape: window.innerWidth > window.innerHeight,
            verticalMobile: window.innerWidth < window.innerHeight && window.innerWidth < 450,
            horizontalMobile: window.innerWidth < window.innerHeight && window.innerHeight < 450,
            verticalPC: window.innerWidth < window.innerHeight && window.innerWidth <= 600 ? "small" : 
                window.innerWidth < window.innerHeight && window.innerWidth <= 900 ? "medium" : 
                window.innerWidth < window.innerHeight ? "large" : "none",
            horizontalPC: window.innerWidth > window.innerHeight && window.innerWidth < 900 ? "small" : 
                window.innerWidth > window.innerHeight && window.innerWidth <= 1200 ? "medium" : 
                window.innerWidth > window.innerHeight ? "large" : "none",
            isMobile: /Mobi|Android|iPhone|iPad|iPod|tablet|Tab/i.test(navigator.userAgent),
            isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
            isIpad: /iPad|Macintosh/.test(navigator.userAgent),
            platform: navigator.userAgent,
            appBarHeight: 60
          }

          setWindowSize((prev: any) => ({ ...prev, ...newWindowSize }));
    }, []);
    
    // Set up initial theme and resize listener
    useEffect(() => {
        setTheme(setAppTheme(1));
        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [handleResize]);

    // Update window size when factor changes
    useEffect(() => {
        handleResize();
    }, [factor, handleResize]);

    // Memoize the return value to prevent creating a new object on every render
    return useMemo(() => ( {
        factor, setFactor,
        appTheme, setTheme,
        redirectURL, setRedirectURL,
        imageCache, setImageCache,
        windowSize, setWindowSize,
        scoreLock, setScoreLock,
        currentHole, setCurrentHole,
        scoreViewTabs, setScoreViewTabs,
        roundReview, setRoundReview,
        toggleScoring, setToggleScoring,
        standingTabs, setStandingTabs,
        leaderboardScoreFormat, setLeaderboardScoreFormat,
        isPWA, setIsPWA,
        scoreBlockID, setScoreBlockID,
        endOfRoundPopupOpen, setEndOfRoundPopupOpen,
        toolsOpen, setToolsOpen,
        toolsTab, setToolsTab,
    } ), [
        factor, appTheme, redirectURL, imageCache, windowSize, scoreLock, 
        currentHole, scoreViewTabs, roundReview, 
        toggleScoring, standingTabs, leaderboardScoreFormat, isPWA,
        scoreBlockID, endOfRoundPopupOpen, toolsOpen, toolsTab,
    ]);
}